import React, { useState, useEffect, useRef } from 'react';
import { readClimatology, writeClimatology } from '../../utils/weatherClimatologyCache';
import { resolveTemperatureUnit } from '@wander/shared/utils/temperatureFormat.js';

// Weather icon SVGs — branded style (outlined, teal strokes)
const WEATHER_SVGS = {
  'clear': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  'partly-cloudy': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
    </svg>
  ),
  'cloudy': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
    </svg>
  ),
  'rain': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <line x1="8" y1="21" x2="8" y2="23"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="16" y1="21" x2="16" y2="23"/>
    </svg>
  ),
  'heavy-rain': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <line x1="7" y1="21" x2="7" y2="24"/><line x1="11" y1="21" x2="11" y2="24"/><line x1="15" y1="21" x2="15" y2="24"/>
    </svg>
  ),
  'drizzle': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <line x1="10" y1="22" x2="10" y2="23"/><line x1="14" y1="22" x2="14" y2="23"/>
    </svg>
  ),
  'snow': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <path d="M8 22l1-1m3 1l1-1m3 1l1-1"/>
    </svg>
  ),
  'heavy-snow': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#bfdbfe" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <path d="M7 22l1-1m2 1l1-1m2 1l1-1m2 1l1-1"/>
    </svg>
  ),
  'fog': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <line x1="4" y1="22" x2="20" y2="22" strokeDasharray="3,3"/>
    </svg>
  ),
  'thunderstorm': (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z"/>
      <polyline points="13 16 11 20 15 20 13 24"/>
    </svg>
  ),
};

const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Compact weather widget for itinerary day header.
 * Fetches weather from backend /api/weather endpoint.
 *
 * Props:
 * - lat: latitude
 * - lng: longitude
 * - date: ISO date string (YYYY-MM-DD)
 * - cityName: display name for tooltip
 */
const WeatherWidget = ({ lat, lng, date, cityName }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Three-tier resolution: explicit user preference > browser locale > default.
  // Non-US users now default to Celsius instead of Fahrenheit. Persists user
  // overrides in localStorage under the same 'weather-unit' key as before.
  const [useCelsius, setUseCelsius] = useState(() => {
    try {
      const stored = localStorage.getItem('weather-unit');
      const userPref = stored === 'C' || stored === 'F' ? stored : null;
      const locale = (navigator.languages && navigator.languages[0]) || navigator.language || 'en';
      return resolveTemperatureUnit({ userPref, locale }) === 'C';
    } catch {
      return false;
    }
  });
  const fetchedRef = useRef(null); // Track what we've already fetched
  const retryCountRef = useRef(0);

  // Open-Meteo returns Celsius — convert to F when user wants Fahrenheit
  const toF = (c) => c !== null && c !== undefined ? Math.round(c * 9 / 5 + 32) : null;
  const displayTemp = (c) => useCelsius ? c : toF(c);

  useEffect(() => {
    if (!lat || !lng || !date) return;

    const key = `${lat}:${lng}:${date}`;
    if (fetchedRef.current === key && weather) return; // Already fetched this combo successfully
    fetchedRef.current = key;
    retryCountRef.current = 0;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    // Check persistent climatology cache first (90-day TTL, localStorage).
    // Only serves trip-planning dates >14 days out; forecast dates miss and
    // fall through to the network.
    const cachedClimo = readClimatology(lat, lng, date);
    if (cachedClimo) {
      setWeather(cachedClimo);
      setLoading(false);
      return () => controller.abort();
    }

    const fetchWeather = async (attempt = 0) => {
      try {
        const token = localStorage.getItem('sb-access-token') || '';
        const res = await fetch(
          `${API_BASE}/api/weather?lat=${lat}&lng=${lng}&date=${date}`,
          {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!res.ok) throw new Error('Weather unavailable');
        const data = await res.json();
        // Persist climatology responses for future planning visits.
        writeClimatology(lat, lng, date, data);
        setWeather(data);
        setLoading(false);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Auto-retry once after 2 seconds
        if (attempt < 1) {
          retryCountRef.current = attempt + 1;
          setTimeout(() => fetchWeather(attempt + 1), 2000);
        } else {
          setError(err.message);
          setLoading(false);
        }
      }
    };

    // Stagger requests — add random delay (0-1500ms) to avoid simultaneous calls
    const stagger = Math.random() * 1500;
    const timer = setTimeout(() => fetchWeather(0), 500 + stagger);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [lat, lng, date]);

  if (loading) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-400">
        <div className="w-3 h-3 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
        Weather...
      </div>
    );
  }

  if (error || !weather) return null;

  const isRainLikely = weather.precipProbability > 60;
  const isClimate = weather.source === 'climate';
  const icon = WEATHER_SVGS[weather.icon] || WEATHER_SVGS['clear'];

  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition-colors ${
        isRainLikely
          ? 'bg-amber-50 border-amber-200 text-amber-700'
          : 'bg-white border-gray-200 text-gray-600'
      }`}
      title={
        isClimate
          ? `Historical average for ${cityName || 'this location'} in ${new Date(date).toLocaleString('en', { month: 'long' })}`
          : `Forecast for ${cityName || 'this location'} on ${date}`
      }
    >
      {icon}
      <span>
        {weather.high !== null ? `${displayTemp(weather.high)}\u00B0` : '--'}
        {weather.low !== null && <span className="text-gray-400 mx-0.5">/</span>}
        {weather.low !== null && <span className="text-gray-400">{displayTemp(weather.low)}\u00B0</span>}
      </span>
      {weather.precipProbability > 20 && (
        <span className={`${isRainLikely ? 'text-amber-600' : 'text-blue-400'}`}>
          {weather.precipProbability}%
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none" className="inline ml-0.5 -mt-0.5">
            <path d="M12 2c-5.33 8-8 12.33-8 14a8 8 0 1016 0c0-1.67-2.67-6-8-14z"/>
          </svg>
        </span>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); setUseCelsius(v => { const next = !v; try { localStorage.setItem('weather-unit', next ? 'C' : 'F'); } catch {} return next; }); }}
        className="text-[10px] font-bold text-gray-400 hover:text-teal-600 transition-colors ml-0.5 cursor-pointer"
        title={useCelsius ? 'Switch to Fahrenheit' : 'Switch to Celsius'}
      >
        {useCelsius ? '\u00B0C' : '\u00B0F'}
      </button>
      {isClimate && (
        <span className="text-gray-400 text-[10px]">avg</span>
      )}
    </div>
  );
};

export default React.memo(WeatherWidget);
